import React from 'react';
import MaterialTable from 'material-table';
import tableIcons from "./MaterialTableIcons";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useGetEmployeeSalesToday, useGetEmployeeSalesLastMTD, useGetEmployeeSalesMTD, useGetEmployeeSalesLastTwoMTD, useGetEmployeeSpecificSalesMTD } from '../../../../queries/Leaderboards';
import './index.scss';
import { useGetSitesTable } from "../../../../queries/useGetSites";
import { useParams } from 'react-router';

const theme = createTheme({ /* your theme configurations */ });

const RANK_COLORS: any = {
	"Platinum": '#E5E4E2',
	"Gold": '#D4AF37',
	"Silver": '#C0C0C0',
	"Bronze": '#CD7F32',
	"Unranked": 'transparent'
};

function getRankFromPercentile(percentile: number): string {
	if (percentile >= 97) return 'Platinum';
	if (percentile >= 85) return 'Gold';
	if (percentile >= 70) return 'Silver';
	if (percentile >= 50) return 'Bronze';
	return 'Unranked';
}

function addRanksToData(data: any[]): any[] {
	const sorted = [...data].sort((a, b) => (b.lastMonth || 0) - (a.lastMonth || 0));
	const n = sorted.length;
	const rankMap = new Map<string, string>();
	const overallRankMap = new Map<string, number>();
	sorted.forEach((row, index) => {
		const percentile = n > 0 ? ((n - index) / n) * 100 : 0;
		rankMap.set(row.name, getRankFromPercentile(percentile));
		overallRankMap.set(row.name, index + 1); // 1st place = most sign ups
	});
	return data.map(row => ({
		...row,
		rank: rankMap.get(row.name) || 'Unranked',
		overallRank: overallRankMap.get(row.name) ?? 0
	}));
}

/**
 * Adds "rankPositionMoved" to structuredData: previousRank - currentRank.
 * Positive = moved up (green), negative = moved down (red).
 * Uses overallRank from both datasets (by lastMonth / previous period).
 */
function addRankPositionMoved(structuredData: any[], structuredDataLastTwoMtd: any[]): any[] {
	const previousRankByName = new Map<string, number>();
	structuredDataLastTwoMtd.forEach((row: any) => {
		if (row.name != null && row.overallRank != null) {
			previousRankByName.set(row.name, row.overallRank);
		}
	});
	return structuredData.map(row => {
		const currentRank = row.overallRank ?? 0;
		const previousRank = previousRankByName.get(row.name);
		const rankPositionMoved =
			previousRank != null ? previousRank - currentRank : null;
		return { ...row, rankPositionMoved };
	});
}

/**
 * Adds "ultimateMixPct" — the share of a rep's month-to-date signups that were Ultimate.
 * Both inputs are already on the row (currentMonth / ultimateSalesMtd), so this needs no
 * new TrueBlue endpoint. Null when the rep has no MTD signups yet, so an empty month is
 * distinguishable from a real 0%.
 */
function addUltimateMixPct(data: any[]): any[] {
	return data.map(row => {
		const total = row.currentMonth ?? 0;
		const ultimate = row.ultimateSalesMtd ?? 0;
		return { ...row, ultimateMixPct: total > 0 ? (ultimate / total) * 100 : null };
	});
}

const midwest_regions: any = {
    // North
    57: 0,
    52: 0,
    53: 0,
    54: 0,
    51: 0,
    59: 0,
    61: 0,
    66: 0,
    70: 0,
    68: 0,
    64: 0,
    67: 0,
    69: 0,
    71: 0,
    74: 0,
    73: 0,
    75: 0,
    76: 0,
    77: 0,
    72: 0,
    // South
    1: 1,
    2: 1,
    3: 1,
    4: 1,
    5: 1,
    6: 1,
    7: 1,
    8: 1,
    9: 1,
    55: 1,
    50: 1,
    56: 1,
    58: 1,
    60: 1,
    62: 1,
    63: 1,
    65: 1,
    10: 1
}

const table_schema_cleanfreak: any[] = [
	{
		title: 'Brand Rank',
		field: 'rank',
		render: (rowData: any) => rowData.rank,
		cellStyle: (rowValue: any) => ({
			backgroundColor: RANK_COLORS[rowValue]
		})
	},
    {   
		title: 'Name',                     
        field: 'name', 
    },
    {   
		title: 'Today',                  
        field: 'today',  
    },
	{
		title: 'Location',
		field: 'location'
	},
	{
		title: "Region", 
		field: "region", 
		render: (rowData: any) => {
			return rowData["region"] === 1 ? "North" : "South"
		},
		hidden: true 
	},
    {   
		title: 'This Month',                    
        field: 'currentMonth', 
        defaultSort: 'desc'
	},
	{
		title: 'Ultimate Sales MTD',
		field: 'ultimateSalesMtd',
	},
	{
		title: 'Ultimate %',
		field: 'ultimateMixPct',
		render: (rowData: any) => {
			const pct = rowData.ultimateMixPct;
			if (pct == null) return '—';
			return `${Math.round(pct * 10) / 10}%`;
		}
	},
	{   
		title: 'Last Month',                    
		field: 'lastMonth', 
	},
	{
		title: 'Rank position moved',
		field: 'rankPositionMoved',
		render: (rowData: any) => {
			const v = rowData.rankPositionMoved;
			if (v == null) return '—';
			return v > 0 ? `+${v}` : v;
		},
		cellStyle: (rankPositionMoved: any) => {
			const v = rankPositionMoved;
			if (v == null || v === 0) return {};
			return { backgroundColor: v > 0 ? '#c8e6c9' : '#ffcdd2' };
		}
	},
]

const table_schema_rainstorm: any[] = [
	{
		title: 'Brand Rank',
		field: 'rank',
		render: (rowData: any) => rowData.rank,
		cellStyle: (rowValue: any) => ({
			backgroundColor: RANK_COLORS[rowValue]
		})
	},
    {   
		title: 'Name',                     
        field: 'name', 
    },
    {   
		title: 'Today',                  
        field: 'today',  
    },
	{
		title: 'Location',
		field: 'location'
	},
	{
		title: "Region", 
		field: "region", 
		render: (rowData: any) => {
			return rowData["region"] === 0 ? "North" : "South"
		},
		hidden: true
	},
    {   
		title: 'This Month',                    
        field: 'currentMonth', 
        defaultSort: 'desc'
    },
	{
		title: 'Ultimate Sales MTD',
		field: 'ultimateSalesMtd',
	},
	{
		title: 'Ultimate %',
		field: 'ultimateMixPct',
		render: (rowData: any) => {
			const pct = rowData.ultimateMixPct;
			if (pct == null) return '—';
			return `${Math.round(pct * 10) / 10}%`;
		}
	},
	{   
		title: 'Last Month',                    
		field: 'lastMonth', 
	},
	{
		title: 'Rank position moved',
		field: 'rankPositionMoved',
		render: (rowData: any) => {
			const v = rowData.rankPositionMoved;
			if (v == null) return '—';
			return v > 0 ? `+${v}` : v;
		},
		cellStyle: (rankPositionMoved: any) => {
			const v = rankPositionMoved;
			if (v == null || v === 0) return {};
			return { backgroundColor: v > 0 ? '#c8e6c9' : '#ffcdd2' };
		}
	},
]

const structureMixedData = (employeeMtdData: any, employeeLastMtdData: any, employeeDataToday: any, siteData: any, employeeSpecificMtdData?: any): any => {
	let dataStruct: any = {};

	for(const entry of employeeDataToday){
		dataStruct[entry["item"]] = {
			"today": entry["qty"],
			"currentMonth": 0,
			"lastMonth": 0,
			"location": siteData[entry["site"]],
			"region": midwest_regions[entry["site"]],
			"name": entry["item"],
			"ultimateSalesMtd": 0
		}
	}

	for(const entry of employeeMtdData){
		try{
			dataStruct[entry["item"]]["currentMonth"] += entry["qty"];
		}catch{
			dataStruct[entry["item"]] = {
				"today": 0,
				"currentMonth": entry["qty"],
				"lastMonth": 0,
				"location": siteData[entry["site"]],
				"region": midwest_regions[entry["site"]],
				"name": entry["item"],
				"ultimateSalesMtd": 0
			}
		}
	}

	for(const entry of employeeLastMtdData){
		try{
			dataStruct[entry["item"]]["lastMonth"] += entry["qty"];
		}catch{
			dataStruct[entry["item"]] = {
				"today": 0,
				"currentMonth": 0,
				"lastMonth": entry["qty"],
				"location": siteData[entry["site"]],
				"region": midwest_regions[entry["site"]],
				"name": entry["item"],
				"ultimateSalesMtd": 0
			}
		}
	}

	if (employeeSpecificMtdData) {
		for (const entry of employeeSpecificMtdData) {
			try {
				dataStruct[entry["item"]]["ultimateSalesMtd"] += entry["qty"];
			} catch {
				dataStruct[entry["item"]] = {
					"today": 0,
					"currentMonth": 0,
					"lastMonth": 0,
					"location": siteData[entry["site"]],
					"region": midwest_regions[entry["site"]],
					"name": entry["item"],
					"ultimateSalesMtd": entry["qty"]
				};
			}
		}
	}

	return Object.values(dataStruct).map( value => value );
}

const EmployeeRanks = ({}) => {
    
	const { brand } = useParams();

	const {
		data: employeeDataToday, 
		isFetching: isFetchingEmployeeDataToday, 
		isError: isErrorEmployeDataToday
	} = useGetEmployeeSalesToday();

    const {
		data: employeeLastMtdData, 
		isFetching: isFetchingEmployeeLastDataMTD, 
		isError: isErrorEmployeeLastDataMTD
	} = useGetEmployeeSalesLastMTD();

	const {
		data: employeeLastTwoMtdData, 
		isFetching: isFetchingEmployeeLastTwoDataMTD, 
		isError: isErrorEmployeeLastTwoDataMTD
	} = useGetEmployeeSalesLastTwoMTD();

    const {
		data: employeeMtdData, 
		isFetching: isFetchingEmployeeDataMTD, 
		isError: isErrorEmployeeDataMTD
	} = useGetEmployeeSalesMTD();

	const {
		data: employeeSpecificMtdData,
		isFetching: isFetchingEmployeeSpecificMTD,
		isError: isErrorEmployeeSpecificMTD
	} = useGetEmployeeSpecificSalesMTD();

	const {
		data: siteData,
		isFetching: isFetchingSites,
		isError: isErrorSites,
	} = useGetSitesTable();

    if ( 
		(isFetchingEmployeeLastDataMTD || isFetchingEmployeeDataMTD || isFetchingEmployeeDataToday || isFetchingSites || isFetchingEmployeeLastTwoDataMTD || isFetchingEmployeeSpecificMTD) 
		&& (!employeeMtdData || !employeeLastMtdData || !employeeDataToday || !siteData || !employeeLastTwoMtdData || !employeeSpecificMtdData) 
	)
        return <>Loading...</>

	const structuredData = addRanksToData(structureMixedData(employeeMtdData, employeeLastMtdData, employeeDataToday, siteData, employeeSpecificMtdData));
	const structuredDataLastTwoMtd = addRanksToData(structureMixedData(employeeLastMtdData, employeeLastTwoMtdData, employeeDataToday, siteData));
	const dataWithRankMoved = addRankPositionMoved(structuredData, structuredDataLastTwoMtd);
	const tableData = addUltimateMixPct(dataWithRankMoved);

	return (
		<ThemeProvider theme={theme}>
			<MaterialTable
				title={'Employee Rankings'}
				data={tableData}
				columns={ brand === 'cleanfreak' ? table_schema_cleanfreak : table_schema_rainstorm }
				options={{
					selection: false,                    
					headerStyle: {
						backgroundColor: brand === 'cleanfreak' ? "red" : "blue",
						color: '#FFF',
						fontSize: 14,
						fontWeight: 1000
					},
					pageSize: 120,
					pageSizeOptions: [],
					paging: false,
					sorting: true,                    
					columnsButton: true,
					search: false
				}}
				icons={tableIcons}
			/>
		</ThemeProvider>
	)
}

export default EmployeeRanks;